import axios, {
  type AxiosError,
  type AxiosResponse,
  type CreateAxiosDefaults,
  type InternalAxiosRequestConfig,
} from "axios";
import { getAuthToken, getRefreshToken, setAuthToken, removeAuthToken, showErrorToast } from "./helper";
import { HTTP_STATUS } from "./constant";
import { captureSentryException } from "./sentry";
import { ENDPOINTS } from "./endpoints";

const DEFAULT_TIMEOUT = 30000; // 30 seconds

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Shared refresh promise — prevents multiple concurrent 401s each firing their own refresh
let pendingRefresh: Promise<boolean> | null = null;

const createAxiosInstance = (config?: CreateAxiosDefaults) => {
  const axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: DEFAULT_TIMEOUT,
    withCredentials: false,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    ...config,
  });

  // Request interceptor — attach Bearer token (current backend) + send cookies (future cookie-based backend)
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  // Response interceptor - Handle common errors
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalConfig = error.config as RetryableConfig;

      // Handle authentication errors
      if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
        const isRefreshRequest = originalConfig.url === ENDPOINTS.REFRESH_TOKEN;

        if (!originalConfig._retry && !isRefreshRequest) {
          originalConfig._retry = true;

          // All concurrent 401s share one refresh attempt — prevents duplicate refresh calls
          if (!pendingRefresh) {
            pendingRefresh = (async (): Promise<boolean> => {
              try {
                const storedRefreshToken = getRefreshToken();
                // Try Bearer-based refresh (current backend) or cookie-based (future backend)
                const res = await axiosInstance.post<{ data?: { accessToken?: string } }>(
                  ENDPOINTS.REFRESH_TOKEN,
                  storedRefreshToken ? { refreshToken: storedRefreshToken } : undefined
                );
                // Store new access token from response body (current backend)
                const newAccessToken = res.data?.data?.accessToken;
                if (newAccessToken) setAuthToken(newAccessToken);
                return true;
              } catch {
                return false;
              } finally {
                pendingRefresh = null;
              }
            })();
          }

          const refreshed = await pendingRefresh;
          if (refreshed) {
            return axiosInstance(originalConfig);
          }
        }

        // Refresh failed, already retried, or this IS the refresh request — force logout
        removeAuthToken();
        showErrorToast("Session expired. Please login again.");
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      }

      if (error.response?.status && error.response.status >= 500) {
        captureSentryException(error, {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response.status,
        });
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export const httpClient = createAxiosInstance();
export default httpClient;
